import React from 'react'
import { Alert, Box, Chip, Stack, Typography } from '@mui/material'

const DEFAULT_CONFIG = Object.freeze({
  resolverKey: 'workflow.current_stage',
  mode: 'progress_stage',
  prefix: 'ETAPA',
  showStageNumber: true,
  showQuotes: false,
  shape: 'rounded',
  emptyStateMode: 'message',
  customLabel: '',
  showMetrics: true,
})

const toSafeObject = (value) => (value && typeof value === 'object' ? value : {})

const getWorkflowRoot = (rawResolvers) => {
  if (!rawResolvers || typeof rawResolvers !== 'object') return null
  if (rawResolvers.workflow && typeof rawResolvers.workflow === 'object') return rawResolvers.workflow
  return rawResolvers
}

const normalizeConfig = (config = {}) => {
  const safeConfig = {
    ...DEFAULT_CONFIG,
    ...toSafeObject(config),
  }

  // Legacy compatibility mappings
  if (safeConfig.displayMode && !safeConfig.mode) {
    safeConfig.mode =
      safeConfig.displayMode === 'billable_stage' ? 'billable_stage' : 'progress_stage'
  }

  if (safeConfig.emptyBehavior && !safeConfig.emptyStateMode) {
    safeConfig.emptyStateMode = safeConfig.emptyBehavior
  }

  if (safeConfig.mode === 'billable_stage') {
    safeConfig.resolverKey = 'workflow.current_billable_stage'
  }

  return safeConfig
}

const resolveStageByResolverKey = ({ workflowRoot, workflowState, resolverKey, mode }) => {
  if (resolverKey === 'workflow.current_billable_stage' || mode === 'billable_stage') {
    return (
      workflowRoot?.current_billable_stage ||
      workflowState?.currentState?.currentBillableStage ||
      null
    )
  }

  return workflowRoot?.current_stage || workflowState?.currentState?.stage || null
}

const resolveChipVariant = (shape) => {
  if (shape === 'flat') return 'filled'
  if (shape === 'chevron') return 'filled'
  return 'outlined'
}

const resolveChipBorderRadius = (shape) => {
  if (shape === 'flat') return 0.75
  if (shape === 'chevron') return 0.5
  return 16
}

const quoteIfNeeded = (value, showQuotes) => {
  if (!showQuotes) return value
  return `"${value}"`
}

const metricLabel = (label, value) => {
  const readable =
    value === null || value === undefined
      ? 'sin evidencia'
      : `${value} día${value === 1 ? '' : 's'}`
  return `${label}: ${readable}`
}

export default function WorkflowStageBanner({
  workflowState = null,
  resolvers = null,
  config = {},
}) {
  const normalizedConfig = normalizeConfig(config)
  const workflowRoot = getWorkflowRoot(resolvers)

  const selectedStage = resolveStageByResolverKey({
    workflowRoot,
    workflowState,
    resolverKey: normalizedConfig.resolverKey,
    mode: normalizedConfig.mode,
  })

  if (!selectedStage) {
    if (normalizedConfig.emptyStateMode === 'hide') {
      return null
    }

    return (
      <Alert severity="info" variant="outlined">
        No hay etapa disponible para el resolver configurado.
      </Alert>
    )
  }

  const stageName = quoteIfNeeded(selectedStage?.name || 'Sin nombre', normalizedConfig.showQuotes)
  const stageNumberPrefix = normalizedConfig.showStageNumber
    ? `${normalizedConfig.prefix} ${selectedStage?.stageNumber ?? '-'}`
    : normalizedConfig.prefix

  const commentMetric = workflowRoot?.metrics?.days_since_last_comment?.value ?? null
  const documentMetric = workflowRoot?.metrics?.days_since_last_document?.value ?? null

  const resolverHint =
    normalizedConfig.resolverKey === 'workflow.current_billable_stage'
      ? 'Etapa facturable'
      : 'Etapa de proceso'

  return (
    <Alert severity={selectedStage?.isBillable ? 'success' : 'info'} sx={{ alignItems: 'flex-start' }}>
      <Stack spacing={1}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {normalizedConfig.customLabel || resolverHint}
          </Typography>
          <Chip
            size="small"
            color={selectedStage?.isBillable ? 'success' : 'info'}
            variant={resolveChipVariant(normalizedConfig.shape)}
            label={`${stageNumberPrefix}: ${stageName}`}
            sx={{ borderRadius: resolveChipBorderRadius(normalizedConfig.shape) }}
          />
        </Box>

        {normalizedConfig.showMetrics ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              label={metricLabel('Días desde último comentario', commentMetric)}
            />
            <Chip
              size="small"
              variant="outlined"
              label={metricLabel('Días desde último documento', documentMetric)}
            />
          </Stack>
        ) : null}
      </Stack>
    </Alert>
  )
}
